import { Popover, Tooltip } from 'antd';
import classNames from 'classnames';
import { CHART_GROUPS, CHART_MAP, ChartType, EXTRA_TYPES } from './constants';
import ImagePlanTag from './ImagePlanTag';
import styles from './index.module.scss';

const POPOVER_STYLES = { root: { zIndex: 1050 }, body: { padding: 0 } };

const parseChartTypes = (sectionData) =>
    sectionData?.selectedChartType
        ? typeof sectionData.selectedChartType === 'string'
            ? sectionData.selectedChartType.split(',').filter(Boolean)
            : sectionData.selectedChartType
        : [];

// 图表类型选择 Popover
function ChartSelectorContent({
    sectionId,
    parentId,
    sectionData,
    grandParentId = null,
    isPremium,
    updateSectionChart,
    setChartPopoverKey,
}) {
    const curChartTypes = parseChartTypes(sectionData);
    const curChartTypeCn = sectionData?.chartTypeCn || {};
    const hasCustom = curChartTypes.includes(ChartType.OTHER);
    const update = (chartUpdate) =>
        updateSectionChart(sectionId, parentId, chartUpdate, grandParentId);

    return (
        <div className={styles.chartSelector}>
            {(curChartTypes.length > 0 || isPremium) && (
                <div className={styles.chartSelectedSection}>
                    <div className={styles.chartSectionHeader}>已配置图表</div>
                    <div className={styles.chartSelectedList}>
                        {curChartTypes
                            .filter((type) => type !== ChartType.OTHER)
                            .map((type) => {
                                const cfg = CHART_MAP[type];
                                const IconComp = cfg?.icon;
                                return (
                                    <div key={type} className={styles.chartListItem}>
                                        <div className={styles.chartListItemInfo}>
                                            {IconComp && (
                                                <div className={styles.chartListItemIcon}>
                                                    <IconComp size={16} />
                                                </div>
                                            )}
                                            <span className={styles.chartListItemName}>
                                                {curChartTypeCn[type] || cfg?.label || type}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.chartListItemRemove}
                                            aria-label="移除该图表"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                update({ action: 'toggle', chartType: type });
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                );
                            })}
                        {/* 自定义图：仅学术特调可编辑 */}
                        {(isPremium || hasCustom) && (
                            <>
                                <div className={styles.chartListItem}>
                                    <div className={styles.chartListItemInfo}>
                                        <div className={styles.chartListItemIcon}>✨</div>
                                        <span className={styles.chartListItemName}>自定义图</span>
                                        {isPremium && (
                                            <span className={styles.chartAiBadge}>AI</span>
                                        )}
                                    </div>
                                    {hasCustom && (
                                        <button
                                            type="button"
                                            className={styles.chartListItemRemove}
                                            aria-label="移除自定义图"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                update({
                                                    action: 'toggle',
                                                    chartType: ChartType.OTHER,
                                                });
                                            }}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                                <div className={styles.chartCustomInput}>
                                    <textarea
                                        className={styles.chartPromptBox}
                                        placeholder={
                                            isPremium
                                                ? '输入你想要的图表描述，AI 将为你生成'
                                                : '自定义图表（仅学术特调可编辑）'
                                        }
                                        value={curChartTypeCn[ChartType.OTHER] || ''}
                                        disabled={!isPremium}
                                        rows={2}
                                        onChange={(e) =>
                                            update({
                                                action: 'updateCustom',
                                                customText: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            <div className={styles.chartLibrary}>
                {CHART_GROUPS.map((group) => (
                    <div key={group.title}>
                        <div className={styles.chartCategoryTitle}>{group.title}</div>
                        <div className={styles.chartGrid}>
                            {group.charts.map((chart) => {
                                const IconComp = chart.icon;
                                const isSelected = curChartTypes.includes(chart.type);
                                return (
                                    <button
                                        type="button"
                                        key={chart.type}
                                        aria-pressed={isSelected}
                                        className={classNames(styles.chartGridItem, {
                                            [styles.active]: isSelected,
                                        })}
                                        onClick={() =>
                                            update({
                                                action: 'toggle',
                                                chartType: chart.type,
                                                chartTypeCn: { [chart.type]: chart.label },
                                            })
                                        }
                                    >
                                        <span className={styles.chartGridIcon}>
                                            <IconComp size={18} />
                                        </span>
                                        <span className={styles.chartGridName}>{chart.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
            <div className={styles.chartActionBar}>
                <button
                    type="button"
                    className={styles.chartBtnClear}
                    onClick={() => update({ action: 'clearAll' })}
                >
                    清空
                </button>
                <button
                    type="button"
                    className={styles.chartBtnDone}
                    onClick={() => setChartPopoverKey(null)}
                >
                    完成
                </button>
            </div>
        </div>
    );
}

// 联网搜索关键词 Popover
function DataKeywordContent({
    sectionId,
    parentId,
    sectionData,
    grandParentId = null,
    updateSectionDataKeyword,
    setDataPopoverKey,
}) {
    const curKeyword = sectionData?.extraTypeCn?.DATA || '';
    const save = (value) => updateSectionDataKeyword(sectionId, parentId, value, grandParentId);
    return (
        <div className={styles.dataKeywordPanel}>
            <div className={styles.dataKeywordHeader}>联网搜索关键词</div>
            <div className={styles.dataKeywordHint}>
                输入需要搜索的数据描述，AI 将在生成时联网查找相关公开数据
            </div>
            <textarea
                className={styles.dataKeywordInput}
                placeholder="如：2023-2024高校宿舍用户占比 | 支撑用户群体权重划分的量化数据"
                defaultValue={curKeyword}
                rows={3}
                autoFocus
                onBlur={(e) => save(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        save(e.target.value);
                        setDataPopoverKey(null);
                    }
                }}
            />
            <div className={styles.dataKeywordActions}>
                {curKeyword && (
                    <button
                        type="button"
                        className={styles.dataKeywordBtnClear}
                        onClick={() => {
                            save('');
                            setDataPopoverKey(null);
                        }}
                    >
                        取消搜索
                    </button>
                )}
                <button
                    type="button"
                    className={styles.dataKeywordBtnDone}
                    onClick={() => setDataPopoverKey(null)}
                >
                    确定
                </button>
            </div>
        </div>
    );
}

/** 插入按钮组：图片选图表类型，联网搜索填关键词，其余直接切换 */
export function SectionExtraButtons({
    sectionId,
    parentId,
    existingExtras = [],
    grandParentId = null,
    sectionData = {},
    isPremium = false,
    chartPopoverKey,
    dataPopoverKey,
    setChartPopoverKey,
    setDataPopoverKey,
    toggleExtraContent,
    updateSectionChart,
    updateSectionDataKeyword,
}) {
    const popoverKey = `${sectionId}_${parentId || ''}_${grandParentId || ''}`;
    const extras = existingExtras || [];

    return (
        <div className={styles.extraButtonGroup}>
            {Object.values(EXTRA_TYPES).map((type) => {
                const isActive = extras.includes(type.key);
                // 桌面只显示图标（悬停看提示）；手机上按钮带短标签，样式里按宽度切换
                const button = (title, onClick) => (
                    <Tooltip title={title}>
                        <button
                            type="button"
                            aria-label={title}
                            aria-pressed={isActive}
                            className={classNames(styles.extraTypeBtn, {
                                [styles.active]: isActive,
                            })}
                            style={{ '--type-color': type.color }}
                            onClick={onClick}
                        >
                            {type.icon}
                            <span className={styles.extraTypeLabel}>{type.short}</span>
                        </button>
                    </Tooltip>
                );

                if (type.key === 'image') {
                    return (
                        <Popover
                            key={type.key}
                            content={
                                <ChartSelectorContent
                                    sectionId={sectionId}
                                    parentId={parentId}
                                    sectionData={sectionData}
                                    grandParentId={grandParentId}
                                    isPremium={isPremium}
                                    updateSectionChart={updateSectionChart}
                                    setChartPopoverKey={setChartPopoverKey}
                                />
                            }
                            trigger="click"
                            placement="bottomRight"
                            open={chartPopoverKey === popoverKey}
                            onOpenChange={(visible) =>
                                setChartPopoverKey(visible ? popoverKey : null)
                            }
                            styles={POPOVER_STYLES}
                        >
                            {button(isActive ? '编辑图表类型' : '插入图片', (e) =>
                                e.stopPropagation(),
                            )}
                        </Popover>
                    );
                }

                if (type.key === 'webSearch') {
                    const keyword = sectionData?.extraTypeCn?.DATA;
                    return (
                        <Popover
                            key={type.key}
                            content={
                                <DataKeywordContent
                                    sectionId={sectionId}
                                    parentId={parentId}
                                    sectionData={sectionData}
                                    grandParentId={grandParentId}
                                    updateSectionDataKeyword={updateSectionDataKeyword}
                                    setDataPopoverKey={setDataPopoverKey}
                                />
                            }
                            trigger="click"
                            placement="bottomRight"
                            open={dataPopoverKey === popoverKey}
                            onOpenChange={(visible) =>
                                setDataPopoverKey(visible ? popoverKey : null)
                            }
                            styles={POPOVER_STYLES}
                            destroyOnHidden
                        >
                            {button(
                                isActive ? keyword || '编辑搜索关键词' : '插入联网搜索数据',
                                (e) => e.stopPropagation(),
                            )}
                        </Popover>
                    );
                }

                return (
                    <span key={type.key}>
                        {button(isActive ? `取消${type.label}` : `插入${type.label}`, (e) => {
                            e.stopPropagation();
                            toggleExtraContent(sectionId, parentId, type.key, grandParentId);
                        })}
                    </span>
                );
            })}
        </div>
    );
}

/** 已选插入内容的标签（普通引擎大纲） */
export function SectionExtraTags({ extras = [], extraDesc = {}, sectionData = {}, className }) {
    if (!extras?.length) return null;

    return (
        <div className={classNames(styles.extraTagsRow, className)}>
            {extras.map((type) => {
                const config = EXTRA_TYPES[type];
                if (!config) return null;

                if (type === 'image') {
                    const charts = Object.entries(sectionData?.chartTypeCn || {}).filter(
                        ([key, value]) =>
                            CHART_MAP[key] || (typeof value === 'string' && value.trim()),
                    );
                    const fallbackDescription = [
                        extraDesc?.image,
                        sectionData?.extraTypeCn?.DIAGRAM,
                    ].find((value) => typeof value === 'string' && value.trim());
                    const hasDescription = charts.some(
                        ([, value]) => typeof value === 'string' && value.trim(),
                    );
                    return charts.length > 0 && (hasDescription || !fallbackDescription) ? (
                        charts.map(([key, description]) => (
                            <ImagePlanTag
                                key={`image-${key}`}
                                description={description}
                                fallbackName={CHART_MAP[key]?.label}
                            />
                        ))
                    ) : (
                        <ImagePlanTag key={type} description={fallbackDescription} />
                    );
                }

                const desc =
                    type === 'webSearch'
                        ? sectionData?.extraTypeCn?.DATA || extraDesc?.webSearch
                        : extraDesc?.[type];
                // 没有说明文字的标签（表格 / 公式 / 代码）手机上不再重复显示，切换按钮本身已点亮
                return (
                    <div
                        key={type}
                        className={classNames(styles.extraTagItem, { [styles.noDesc]: !desc })}
                        style={{ '--tag-color': config.color }}
                    >
                        <span className={styles.extraTagIcon}>{config.icon}</span>
                        <span className={styles.extraTagLabel}>{config.label}</span>
                        {desc && <span className={styles.extraTagDesc}>：{desc}</span>}
                    </div>
                );
            })}
        </div>
    );
}
